import { Job } from "@core/domains/jobs";
import {
  OffsetPaginationSchema,
  apiEndpoint,
  adminApi as createAdminApi,
  offsetPaginatedResponse,
} from "@core/ports/http";
import { z } from "@core/zod";

import { API_DOMAIN } from "@platform/core";

import { ADMIN_SERVICE } from "../config/index.ts";

const MetricUnitSchema = z.enum(["bytes", "count", "milliseconds", "minutes"]);

export type MetricUnit = z.infer<typeof MetricUnitSchema>;

const MetricDatapointSchema = z.object(
  { timestamp: z.iso.datetime(), value: z.number() },
  {
    timestamp: "The end of the window the value sums, as an ISO 8601 timestamp",
    value:
      "The metric's sum over the evaluation window ending at the timestamp",
  },
);

const MetricSchema = z.object(
  {
    alertThreshold: z.number().optional(),
    datapoints: z.array(MetricDatapointSchema),
    id: z.string(),
    label: z.string(),
    shutdownThreshold: z.number().optional(),
    unit: MetricUnitSchema,
  },
  {
    alertThreshold: "The windowed sum at which the cost guard alerts",
    datapoints: "The metric's windowed sums, one per minute",
    id: "The metric's identifier",
    label: "The metric's display label",
    shutdownThreshold: "The windowed sum at which the cost guard shuts down",
    unit: "The unit the metric's values are measured in",
  },
);

export interface Metric extends z.infer<typeof MetricSchema> {}

export const adminApi = createAdminApi(
  { domain: API_DOMAIN, path: `${ADMIN_SERVICE.LABEL}/admin` },
  {
    getAllMetrics: apiEndpoint({
      response: z.object(
        { items: z.array(MetricSchema) },
        { items: "The cost guard's metrics" },
      ),
    }),
    getTables: apiEndpoint({
      body: OffsetPaginationSchema,
      response: offsetPaginatedResponse(z.string()),
    }),
    syncAllTables: apiEndpoint({
      response: z.object(
        { jobs: z.array(Job.schema) },
        { jobs: "The sync jobs started, one per table of the stage" },
      ),
    }),
    syncTable: apiEndpoint({
      body: z.object(
        { tableName: z.string() },
        { tableName: "The name of the DynamoDB table to sync" },
      ),
      response: z.object(
        { job: Job.schema },
        { job: "The sync job started for the table" },
      ),
    }),
  },
);
